import { getToken } from "../utils/auth";
import { API_BASE_URL } from "./URL_Header";

const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
  "Content-Type": "application/json",
});

const parseError = async (response, fallbackMessage) => {
  let message = fallbackMessage;
  try {
    const data = await response.json();
    message = data.message || data.title || message;
  } catch {
    // ignore parse errors
  }
  const error = new Error(message);
  error.status = response.status;
  throw error;
};

const handleJson = async (response, fallbackMessage) => {
  if (!response.ok) {
    await parseError(response, fallbackMessage);
  }
  if (response.status === 204) return {};
  return response.json();
};

export const fetchReturns = async ({
  page = 0,
  pageSize = 20,
  status = "Available",
  designCode = "",
  typeId = "",
  orderId = "",
  fromUtc = "",
  toUtc = "",
} = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    status: status || "Available",
  });
  if (designCode?.trim()) params.set("designCode", designCode.trim());
  if (typeId) params.set("typeId", String(typeId));
  if (orderId) params.set("orderId", String(orderId));
  if (fromUtc) params.set("fromUtc", fromUtc);
  if (toUtc) params.set("toUtc", toUtc);

  const response = await fetch(`${API_BASE_URL}/Returns/GetReturns?${params.toString()}`, {
    headers: authHeaders(),
  });
  return handleJson(response, "Failed to fetch returns");
};

export const createReturn = async ({ designCode, typeId, caseStyleId, orderId, notes, rejected = false }) => {
  const response = await fetch(`${API_BASE_URL}/Returns/AddReturn`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      designCode,
      typeId: Number(typeId),
      caseStyleId: caseStyleId ? Number(caseStyleId) : null,
      orderId: orderId ? Number(orderId) : null,
      notes: notes || null,
      rejected: !!rejected,
    }),
  });
  return handleJson(response, "Failed to create return");
};

export const updateReturn = async (id, { designCode, typeId, caseStyleId, orderId, notes }) => {
  const response = await fetch(`${API_BASE_URL}/Returns/EditReturn/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify({
      designCode,
      typeId: typeId ? Number(typeId) : null,
      caseStyleId: caseStyleId === "" || caseStyleId == null ? null : Number(caseStyleId),
      orderId: orderId === "" || orderId == null ? null : Number(orderId),
      notes,
    }),
  });
  return handleJson(response, "Failed to update return");
};

export const deleteReturn = async (id, { force = false } = {}) => {
  const params = force ? "?force=true" : "";
  const response = await fetch(`${API_BASE_URL}/Returns/DeleteReturn/${id}${params}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return handleJson(response, "Failed to delete return");
};

export const rejectReturn = async (id, notes) => {
  const response = await fetch(`${API_BASE_URL}/Returns/Reject/${id}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ notes: notes || null }),
  });
  return handleJson(response, "Failed to reject return");
};

export const restoreReturn = async (id) => {
  const response = await fetch(`${API_BASE_URL}/Returns/Restore/${id}`, {
    method: "POST",
    headers: authHeaders(),
  });
  return handleJson(response, "Failed to restore return");
};

export const deleteAllAvailableReturns = async (confirmation) => {
  const response = await fetch(`${API_BASE_URL}/Returns/DeleteAll`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ confirmation }),
  });
  return handleJson(response, "Failed to delete returns");
};

export const previewOrderReturns = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/Returns/OrderPreview/${orderId}`, {
    headers: authHeaders(),
  });
  return handleJson(response, "Failed to preview order returns");
};

export const addReturnsFromOrder = async (orderId, notes) => {
  const response = await fetch(`${API_BASE_URL}/Returns/AddFromOrder`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      orderId: Number(orderId),
      notes: notes || null,
    }),
  });
  return handleJson(response, "Failed to add returns from order");
};

export const fetchAvailableReturnMatches = async (keys) => {
  const response = await fetch(`${API_BASE_URL}/Returns/AvailableMatches`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(keys || []),
  });
  return handleJson(response, "Failed to match available returns");
};
